import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getQuizzes, deleteQuiz, duplicateQuiz, getCascadeInfo } from '../lib/api';
import { QcmPreviewDialog } from '../components/QcmPreviewDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, FileText, Download, Trash2, MoreVertical, BarChart3, Eye, Copy, Loader2 } from 'lucide-react';

import { usePageTitle } from '../hooks/usePageTitle';

export default function QuizList() {
  usePageTitle('QCM');
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewQuiz, setPreviewQuiz] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { quiz_id, titre, exams_count, results_count }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const loadQuizzes = useCallback(async () => {
    try {
      const res = await getQuizzes();
      setQuizzes(res.data.data);
    } catch { /* quiz list load failed */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadQuizzes();
  }, [loadQuizzes]);

  const handleDeleteClick = async (quiz) => {
    try {
      const res = await getCascadeInfo(quiz.quiz_id);
      setDeleteTarget({
        quiz_id: quiz.quiz_id,
        titre: quiz.titre,
        exams_count: res.data.exams_count,
        results_count: res.data.results_count,
      });
    } catch {
      // Fallback if cascade-info fails
      setDeleteTarget({ quiz_id: quiz.quiz_id, titre: quiz.titre, exams_count: 0, results_count: 0 });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await deleteQuiz(deleteTarget.quiz_id);
      const d = res.data;
      const parts = [];
      if (d.deleted_exams > 0) parts.push(`${d.deleted_exams} examen(s)`);
      if (d.deleted_results > 0) parts.push(`${d.deleted_results} résultat(s)`);
      if (d.deleted_pdfs > 0) parts.push(`${d.deleted_pdfs} PDF(s)`);
      const detail = parts.length ? ` + ${parts.join(', ')} supprimé(s)` : '';
      toast.success(`QCM supprimé${detail}`);
      loadQuizzes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
    setDeleteLoading(false);
    setDeleteTarget(null);
  };

  const handleDuplicate = async (quizId) => {
    try {
      const res = await duplicateQuiz(quizId);
      toast.success('QCM dupliqué');
      navigate(`/quizzes/${res.data.quiz_id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur de duplication';
      toast.error(msg);
    }
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="quiz-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Mes QCM</h1>
          <p className="text-muted-foreground mt-1">{quizzes.length} QCM créés</p>
        </div>
        <Link to="/quizzes/new">
          <Button data-testid="create-quiz-btn" className="bg-primary text-primary-foreground hover:bg-blue-600">
            <Plus className="w-4 h-4 mr-2" /> Nouveau QCM
          </Button>
        </Link>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {quizzes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Titre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Classe</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Questions</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Barème</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quizzes.map((q) => (
                  <TableRow key={q.quiz_id} className="hover:bg-muted/30 transition-colors" data-testid={`quiz-row-${q.quiz_id}`}>
                    <TableCell>
                      <Link to={`/quizzes/${q.quiz_id}`} className="font-medium hover:text-primary transition-colors">
                        {q.titre}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary">{q.classe}</span>
                    </TableCell>
                    <TableCell>{q.questions?.length || 0}</TableCell>
                    <TableCell>/{q.bareme}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {q.created_at ? new Date(q.created_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`quiz-menu-${q.quiz_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                          <DropdownMenuItem asChild>
                            <Link to={`/quizzes/${q.quiz_id}`} className="flex items-center gap-2">
                              <Eye className="w-4 h-4" /> Modifier
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(q.quiz_id)} data-testid={`duplicate-quiz-${q.quiz_id}`}>
                            <Copy className="w-4 h-4 mr-2" /> Dupliquer
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPreviewQuiz(q)} data-testid={`download-qcms-${q.quiz_id}`}>
                            <Download className="w-4 h-4 mr-2" /> Aperçu & Télécharger
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/results?quiz=${q.quiz_id}`} className="flex items-center gap-2">
                              <BarChart3 className="w-4 h-4" /> Voir résultats
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteClick(q)} className="text-destructive" data-testid={`delete-quiz-${q.quiz_id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
              <p className="text-muted-foreground mb-4">Aucun QCM créé.</p>
              <Link to="/quizzes/new">
                <Button className="bg-primary text-primary-foreground hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" /> Créer mon premier QCM
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <QcmPreviewDialog quizId={previewQuiz?.quiz_id} quizTitle={previewQuiz?.titre} open={!!previewQuiz} onClose={() => setPreviewQuiz(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-card border-border" data-testid="delete-quiz-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {deleteTarget?.titre} » ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Cette action est irréversible. Seront supprimés :</p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>Le QCM lui-même</li>
                  {deleteTarget?.exams_count > 0 && (
                    <li><strong>{deleteTarget.exams_count}</strong> examen(s) générés et leurs PDFs</li>
                  )}
                  {deleteTarget?.results_count > 0 && (
                    <li><strong>{deleteTarget.results_count}</strong> résultat(s) de scan</li>
                  )}
                  {(!deleteTarget?.exams_count && !deleteTarget?.results_count) && (
                    <li className="text-muted-foreground">Aucune donnée associée</li>
                  )}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading} data-testid="cancel-delete-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
